import { defineStore } from 'pinia'
import { listAnnouncements, markAnnouncementRead } from '@/api/announcements'
import type { Announcement } from '@/api/types'

/**
 * Holds just enough state to badge the nav item with an unread count without
 * every screen re-fetching the full list. The Announcements screen itself
 * still owns its own paginated list (via usePaginated) — this store is not a
 * cache of that, only a running unread count plus a locally-known read set so
 * marking one read doesn't need a full list refetch to update the badge.
 */
export const useAnnouncementsStore = defineStore('announcements', {
  state: () => ({
    unreadCount: 0,
    loaded: false,
  }),
  actions: {
    async refreshUnreadCount() {
      // The API has no dedicated unread-count endpoint, so this walks pages
      // until it has counted every unread one in the current window. Bounded
      // to a few pages — an unread count in the thousands is not something a
      // nav badge needs to be exact about.
      let cursor: string | undefined
      let unread = 0
      let pages = 0
      do {
        const page = await listAnnouncements({ cursor, limit: 50 })
        unread += page.items.filter((a: Announcement) => !a.read).length
        cursor = page.meta.next_cursor ?? undefined
        pages += 1
      } while (cursor && pages < 5)
      this.unreadCount = unread
      this.loaded = true
    },
    async markRead(id: string) {
      await markAnnouncementRead(id)
      this.unreadCount = Math.max(0, this.unreadCount - 1)
    },
  },
})
